// @givmo/app-charity — the Compliance module's mount inside the console shell (CCM-MOD1B;
// shell contract v3 mechanic 3; the Books mount is the template).
//
// ══ ONE GATE, ONE HOST, EVERY PAGE ═════════════════════════════════════════════════════════════
//
// Every compliance route renders `<CharityComplianceMount>`: the package's `ModuleGate` over the
// console's ONE standing read (pending → nothing; a failed read → the retry notice; `locked` /
// `off` / `setting-up` → the tile's own answer with the SAME request door; `absent` → not-found),
// and — only on `link` — `CharityComplianceHost`, which feeds the shared package `orgRef` from the
// active-org context and `moduleStanding` from the same standing read the gate consumed (plus the
// viewer's own reminders switch, off the org console's preferences read). Nothing here observes the
// standing key a second time: that is the measured refetch loop the console context's header
// describes.
//
// ══ WHY THE STANDING REACHES THE PACKAGE AT ALL ═════════════════════════════════════════════════
//
// The gate decides whether the module RENDERS; the package's `moduleStanding` decides whether it
// READS (`@givmo/compliance` → `compliance-standing.ts`). The two agree by construction because
// both derive from one read (`complianceStandingFor`, in the adapter), and the second half is what
// turns a live revocation into "the next render asks the server nothing" rather than "the page
// stays up until its next 403".

import type { ReactNode } from 'react';
import {
  COMPLIANCE_SLUG,
  CalendarPage as SharedCalendarPage,
  ComplianceDocumentsPage as SharedComplianceDocumentsPage,
  FilingSendPage as SharedFilingSendPage,
  FilingWorkbenchPage as SharedFilingWorkbenchPage,
  FilingsPage as SharedFilingsPage,
  ReviewPage as SharedReviewPage,
  SigningPage as SharedSigningPage,
} from '@givmo/compliance';
import { ModuleGate } from '@givmo/org-console';
import { NotFound } from '../../components/not-found';
import { useRequestAccessDoor } from '../../components/request-access-context';
import { useCharityConsole } from '../../console/charity-console-context';
import { charityModuleDefinition } from '../../console/module-table';
import { useModuleNavigate } from '../../console/use-console-squares';
import { useTurnOnHref } from '../../console/use-turn-on-href';
import { CharityComplianceHost } from './compliance-adapter';

export function CharityComplianceMount({ children }: { children: ReactNode }) {
  const { pending, errored, standings, refetch } = useCharityConsole();
  const door = useRequestAccessDoor();
  const navigate = useModuleNavigate();
  const turnOnHref = useTurnOnHref(COMPLIANCE_SLUG);
  const standing = standings?.[COMPLIANCE_SLUG];
  const request = standing?.accessRequest;
  return (
    <ModuleGate
      slug={COMPLIANCE_SLUG}
      // The TILE's word (the class table's label); the pages are Calendar · Filings · Documents.
      label={charityModuleDefinition(COMPLIANCE_SLUG)?.label ?? 'Compliance'}
      read={{ pending, errored, standings }}
      onRetry={refetch}
      renderNotFound={() => <NotFound />}
      turnOnHref={turnOnHref}
      onNavigate={navigate}
      // The request door is withheld on a module the person already holds (409 for a holder).
      onRequestAccess={standing?.held === true ? undefined : () => door.open(COMPLIANCE_SLUG)}
      accessRequest={
        request?.status === 'PENDING'
          ? { status: 'pending' }
          : request?.status === 'DENIED'
            ? { status: 'declined', note: request.resolutionNote ?? null }
            : undefined
      }
    >
      <CharityComplianceHost>{children}</CharityComplianceHost>
    </ModuleGate>
  );
}

// The route components — each a shared page behind the ONE mount. The recorded facts have none:
// their address redirects onto the Calendar (D-CCM-14), and their page stays in the package,
// unmounted, for the row that brings it back. The legacy review queue's page retired
// (CCM-R1-REVIEW-LEGACY-RETIRE); its address is the review's own.

/** `/compliance` — the Calendar, the module's landing (set C Q1). */
export function ComplianceCalendarPage() {
  return (
    <CharityComplianceMount>
      <SharedCalendarPage />
    </CharityComplianceMount>
  );
}

/** `/compliance/filings`, and `/compliance/filings/<id>` with that filing's panel open. */
export function ComplianceFilingsPage() {
  return (
    <CharityComplianceMount>
      <SharedFilingsPage />
    </CharityComplianceMount>
  );
}

/** `/compliance/filings/<id>/rule`: Filings with the panel showing the rule behind it (D2). */
export function ComplianceFilingRulePage() {
  return (
    <CharityComplianceMount>
      <SharedFilingsPage panel="rule" />
    </CharityComplianceMount>
  );
}

/** `/compliance/filings/<id>/form`: the filing's form, prepared (CCM-R1-DOCS; slice 2 §5). */
export function ComplianceFilingFormPage() {
  return (
    <CharityComplianceMount>
      <SharedFilingWorkbenchPage />
    </CharityComplianceMount>
  );
}

/**
 * `/compliance/filings/<id>/send`: the signed filing's send review, its outcomes and the record of
 * an uncertain send (CCM-R1-SUBMIT-WEB; slice 3 §5–§6).
 */
export function ComplianceFilingSendPage() {
  return (
    <CharityComplianceMount>
      <SharedFilingSendPage />
    </CharityComplianceMount>
  );
}

/**
 * `/compliance/review`: what Givmo found when it compared the organization's recorded facts with the
 * rules it evaluates, and the organization's decisions on it (CCM-R1-REVIEW-WEB; D-CCM-51).
 */
export function ComplianceReviewPage() {
  return (
    <CharityComplianceMount>
      <SharedReviewPage />
    </CharityComplianceMount>
  );
}

/**
 * `/compliance/sign/<request id>`: a request someone was asked to sign (CCM-R1-SIGN-WEB; slice 2
 * §6(b)). The address the backend's signing emails link to.
 */
export function ComplianceSigningPage() {
  return (
    <CharityComplianceMount>
      <SharedSigningPage />
    </CharityComplianceMount>
  );
}

export function ComplianceDocumentsPage() {
  return (
    <CharityComplianceMount>
      <SharedComplianceDocumentsPage />
    </CharityComplianceMount>
  );
}
